import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { KeyRound, UserPlus, ShieldCheck, ShieldOff, CheckCircle2, Sparkles, Copy, RefreshCw, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

// Owner-only widget to grant/revoke unlimited-scans and provision test
// accounts on the current environment (preview or production).
// Runs against the same DB the server is bound to — so this fixes the
// "preview vs prod DB drift" problem: no more one-off scripts.

export function OwnerManageAccessCard() {
    const [email, setEmail] = useState('');
    const [reason, setReason] = useState('');
    const [createIfMissing, setCreateIfMissing] = useState(false);
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    // Backfill state
    const [backfilling, setBackfilling] = useState(false);
    const [backfillResult, setBackfillResult] = useState(null);

    async function submit(unlimited) {
        const target = email.trim().toLowerCase();
        if (!target || !target.includes('@')) {
            toast.error('Enter a valid email');
            return;
        }
        setBusy(true);
        try {
            const r = await api.ownerGrantUnlimited({
                email: target,
                unlimited,
                create_if_missing: createIfMissing,
                password: createIfMissing ? password : undefined,
                reason: reason.trim() || undefined,
            });
            setLastResult(r);
            if (r.created) {
                toast.success(
                    `Created ${r.email} · unlimited scans ${r.billing.unlimited_scans ? 'ON' : 'OFF'}`,
                    { duration: 6000 },
                );
            } else {
                toast.success(
                    `${unlimited ? 'Granted' : 'Revoked'} unlimited scans for ${r.email}`,
                );
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Action failed');
        } finally {
            setBusy(false);
        }
    }

    function copyLastEmail() {
        if (lastResult?.email) {
            navigator.clipboard.writeText(lastResult.email);
            toast.success('Email copied');
        }
    }

    async function runBackfill(dryRun) {
        setBackfilling(true);
        try {
            const r = await api.ownerBackfillTax({ dry_run: !!dryRun });
            setBackfillResult({ ...r, dryRunLabel: dryRun ? 'Dry-run' : 'Applied' });
            if (r.reconstructed > 0) {
                toast.success(
                    `${dryRun ? 'Would fix' : 'Fixed'} ${r.reconstructed} bill${r.reconstructed === 1 ? '' : 's'} (${r.scanned} scanned).`,
                    { duration: 6000 },
                );
            } else {
                toast.info(`No bills need tax backfill (${r.scanned} scanned).`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Backfill failed');
        } finally {
            setBackfilling(false);
        }
    }

    return (
        <Card className="p-5 border-border/80" data-testid="owner-manage-access-card">
            <div className="flex items-start gap-3 mb-4">
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0">
                    <KeyRound size={18} />
                </div>
                <div className="min-w-0">
                    <div className="font-heading text-base leading-tight">Manage access</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        Grant unlimited scans to a user or provision a new whitelisted account.
                        Acts on the current environment&apos;s database.
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
                <div>
                    <Label htmlFor="owner-access-email" className="text-xs mb-1.5 block">
                        User email
                    </Label>
                    <Input
                        id="owner-access-email"
                        data-testid="owner-access-email"
                        placeholder="user@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={busy}
                        autoComplete="off"
                    />
                </div>

                <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
                    <div className="min-w-0">
                        <Label htmlFor="owner-access-create" className="text-sm cursor-pointer">
                            Create account if missing
                        </Label>
                        <p className="text-[11px] text-muted-foreground">
                            Provisions a new pre-verified user with the password below.
                        </p>
                    </div>
                    <Switch
                        id="owner-access-create"
                        data-testid="owner-access-create"
                        checked={createIfMissing}
                        onCheckedChange={setCreateIfMissing}
                        disabled={busy}
                    />
                </div>

                {createIfMissing && (
                    <div>
                        <Label htmlFor="owner-access-password" className="text-xs mb-1.5 block">
                            Password for new account (min 8 chars)
                        </Label>
                        <Input
                            id="owner-access-password"
                            data-testid="owner-access-password"
                            type="text"
                            placeholder="e.g. TestPass@123"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={busy}
                            autoComplete="new-password"
                        />
                    </div>
                )}

                <div>
                    <Label htmlFor="owner-access-reason" className="text-xs mb-1.5 block">
                        Reason (recorded in the tenant&apos;s access log)
                    </Label>
                    <Textarea
                        id="owner-access-reason"
                        data-testid="owner-access-reason"
                        placeholder='e.g. "VIP customer, granted per email #2451"'
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        disabled={busy}
                        rows={2}
                        className="resize-none"
                        maxLength={280}
                    />
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                        onClick={() => submit(true)}
                        disabled={busy || !email.trim()}
                        data-testid="owner-access-grant"
                        className="gap-1.5"
                    >
                        {createIfMissing ? <UserPlus size={14} /> : <ShieldCheck size={14} />}
                        {createIfMissing ? 'Create + grant unlimited' : 'Grant unlimited scans'}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => submit(false)}
                        disabled={busy || !email.trim() || createIfMissing}
                        data-testid="owner-access-revoke"
                        className="gap-1.5"
                        title={createIfMissing ? 'Turn off "Create if missing" to revoke' : ''}
                    >
                        <ShieldOff size={14} /> Revoke
                    </Button>
                </div>
            </div>

            {lastResult && (
                <div
                    className="mt-4 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs"
                    data-testid="owner-access-result"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 size={14} className="text-emerald-600" />
                        <span className="font-medium text-sm">
                            {lastResult.created ? 'Account created' : 'Updated'}
                        </span>
                        <button
                            onClick={copyLastEmail}
                            className="ml-auto text-muted-foreground hover:text-foreground p-1 rounded"
                            title="Copy email"
                            data-testid="owner-access-copy-email"
                        >
                            <Copy size={12} />
                        </button>
                    </div>
                    <div className="space-y-1 font-mono">
                        <div><span className="text-muted-foreground">Email:</span> {lastResult.email}</div>
                        <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Unlimited:</span>
                            {lastResult.billing?.unlimited_scans ? (
                                <Badge className="gap-1 text-[10px]">
                                    <Sparkles size={10} /> Yes
                                </Badge>
                            ) : (
                                <Badge variant="outline" className="text-[10px]">No</Badge>
                            )}
                            <span className="text-muted-foreground ml-2">Status:</span>
                            <Badge variant="outline" className="text-[10px]">
                                {lastResult.billing?.status || '—'}
                            </Badge>
                            {lastResult.email_verified && (
                                <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200">
                                    verified
                                </Badge>
                            )}
                        </div>
                        <div className="text-[10px] text-muted-foreground pt-1">
                            Scans used this month: {lastResult.billing?.scan_count_month ?? 0}
                        </div>
                    </div>
                </div>
            )}

            {/* ---- Backfill tax section ------------------------------------ */}
            <div className="mt-6 pt-5 border-t border-dashed border-border/60" data-testid="owner-backfill-section">
                <div className="flex items-start gap-3 mb-3">
                    <div className="h-8 w-8 rounded-lg bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 grid place-items-center shrink-0">
                        <Calculator size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm leading-tight">Backfill tax on historical bills</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            Fixes old bills where OCR missed CGST/SGST/IGST and dumped the amount into round-off.
                            Reconstructs tax from the subtotal-to-total gap when it matches a standard GST rate.
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => runBackfill(true)}
                        disabled={backfilling}
                        data-testid="owner-backfill-dry"
                        className="gap-1.5"
                    >
                        <RefreshCw size={13} className={backfilling ? 'animate-spin' : ''} />
                        Dry-run
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => runBackfill(false)}
                        disabled={backfilling}
                        data-testid="owner-backfill-apply"
                        className="gap-1.5"
                    >
                        <Calculator size={13} />
                        Apply backfill
                    </Button>
                </div>

                {backfillResult && (
                    <div
                        className="mt-3 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs"
                        data-testid="owner-backfill-result"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 size={13} className="text-emerald-600" />
                            <span className="font-medium">
                                {backfillResult.dryRunLabel} — {backfillResult.reconstructed} of {backfillResult.scanned} bills reconstructed
                            </span>
                        </div>
                        <div className="text-muted-foreground space-y-0.5">
                            <div>Scanned: <span className="font-mono">{backfillResult.scanned}</span></div>
                            <div>Reconstructed: <span className="font-mono text-emerald-700">{backfillResult.reconstructed}</span></div>
                            <div>Already flagged: <span className="font-mono">{backfillResult.skipped_already_flagged}</span></div>
                            <div>No rate match: <span className="font-mono">{backfillResult.skipped_no_match}</span></div>
                        </div>
                        {backfillResult.sample?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-border/40">
                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Sample changes</div>
                                <div className="space-y-1 max-h-48 overflow-y-auto">
                                    {backfillResult.sample.map((s) => (
                                        <div key={s.id} className="font-mono text-[10px]">
                                            <span className="text-foreground">{s.vendor || 'unknown'}</span>
                                            {' · '}sub ₹{Number(s.subtotal).toFixed(2)}
                                            {' → '}total ₹{Number(s.total).toFixed(2)}
                                            {' · '}tax +₹{Number(s.after_tax).toFixed(2)} ({s.rate_pct}% {s.split})
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
}
